import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {ReduxActions, useAppDispatch, useAppSelector} from "../../redux";
import {
    DialogsReduxSliceStateFieldLogsHistoryData,
    DialogsReduxSliceStateFieldLogsHistoryEntryDataGetLogsArgFilters,
    DialogsReduxSliceStateFieldLogsHistoryEntryDataRecord,
} from "../../redux/slices/dialogs";
import InfiniteScroll from "react-infinite-scroller";
import {Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton, TextField, Theme, Typography} from "@mui/material";
import {DateUtils, SearchDataSettings, Timeline, TimelineProps, useDebounced, useIsMounted, useSearch, UseSearchFunctionArg} from "../../../@bizkeytech";
import {DefaultPaginationInfo} from "../../../core/constants/enums";
import {ArrowForward, SearchRounded} from "@mui/icons-material";
import useInfiniteScrollSearchUtils from "../../../@bizkeytech/base/ui/hooks/use-infinite-scroll-search-utils";

const FieldLogsHistoryDialog: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(state => state.dialogs?.fieldLogsHistory);
    const [data, setData] = useState<Array<DialogsReduxSliceStateFieldLogsHistoryEntryDataRecord>>([]);
    const [replacement, setReplacement] = useState<DialogsReduxSliceStateFieldLogsHistoryEntryDataRecord>();
    const [keywords, setKeywords, deferredKeywords, removeDeferredTimer] = useDebounced("", 1000);
    const isMounted = useIsMounted();

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;
    const {getLogs} = (state?.data ?? {});

    const abortControllerRef = useRef(new AbortController());

    /**
     * Searches for list of data for exporting or for showing the list of items in the view
     *
     * * Calls the api regarding this action and updated the pagination info and sets the [reports] value in the inner state if not for
     * exporting Otherwise, returns the crud response.
     */
    const fetchEntries = useCallback<(args: UseSearchFunctionArg<DialogsReduxSliceStateFieldLogsHistoryEntryDataGetLogsArgFilters>) => Promise<void>>(async (args) => {
        const paginated = state.data?.paginated ?? false;
        if ((args.paginationInfo?.currentPage ?? 0) > 1 && !paginated)
            return
        const abortControllerSignal = abortControllerRef.current.signal;
        const response = await getLogs?.(args);
        if (!isMounted())
            return;
        if (abortControllerSignal.aborted) {
            // dialog opened and closed so do nothing,
            return;
        }
        if (response?.resultFlag) {
            setData(prevState => [...prevState, ...response.data?.items ?? []]);
            args.setPaginationInfo(prevState => ({
                ...prevState!,
                length: paginated ? response?.data?.totalCount ?? 0 : response?.data?.items?.length ?? 0,
            }))
        }
    }, [isMounted, getLogs, state.data?.paginated])

    const initialSearchSettings = useMemo<SearchDataSettings<DialogsReduxSliceStateFieldLogsHistoryEntryDataGetLogsArgFilters>>(() => ({
        paginationInfo: DefaultPaginationInfo,
    }), []);

    const searchData = useSearch<DialogsReduxSliceStateFieldLogsHistoryEntryDataGetLogsArgFilters>(fetchEntries, initialSearchSettings, false);
    const {setFilters, setLoading, setPaginationInfo} = searchData;

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (!state?.open) {
            removeDeferredTimer();
            abortControllerRef.current.abort();
            return;
        }
        abortControllerRef.current = new AbortController();
        setReplacement(undefined);
        setLoading(false);
        setPaginationInfo(initialSearchSettings.paginationInfo);
        setData([]);
    }, [state?.open, removeDeferredTimer, setLoading, setPaginationInfo, initialSearchSettings.paginationInfo]);

    /**
     * With each change in the [searchData.filters?.keyword]:
     * - resets the data list
     */
    useEffect(() => {
        setData([]);
        setLoading(true);
        setFilters?.({keyword: deferredKeywords ?? ""});
    }, [setFilters, deferredKeywords, setLoading]);

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback((successful = false, data: DialogsReduxSliceStateFieldLogsHistoryData | null = null) => {
        if (state?.onClose) {
            state.onClose(successful ? "successful" : "leave", data);
        }
        dispatch(ReduxActions.dialogs.fieldLogsHistory({open: false}));
    }, [state, dispatch])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick)
                    closeDialog()
                break;
            case "closeClick":
                if (!state.hideCloseButton)
                    closeDialog()
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown)
                    closeDialog()
                break;
            default:
                break;
        }
    }, [closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])

    const {loadMoreData, hasMoreToLoad} = useInfiniteScrollSearchUtils({searchData, dataLength: data.length});

    const timelineItems = useMemo<TimelineProps['items']>(() =>
        data.map(entry => {
            let disabled: boolean;
            if (state?.data?.getDataUniqueKey) {
                disabled = state.data.getDataUniqueKey(entry.data) === state.data.getDataUniqueKey(state?.data?.currentValue?.data);
            } else {
                disabled = entry.data === state?.data?.currentValue?.data;
            }
            return ({
                key: entry.key,
                disabled: disabled,
                contentPrimaryText: entry.title ?? "--",
                oppositeContentPrimaryText: DateUtils.serverToLocal(entry.submittedAt).format(DateUtils.dateTimeFormat),
                oppositeContentSecondaryText: entry.submittedBy,
            });
        }), [data, state.data]);

    const selectedTimelineItemsKeys = useMemo<TimelineProps['selectedItemKeys']>(() => {
        if (replacement?.key)
            return [replacement.key]
        return [];
    }, [replacement?.key])

    const loadingTimelineItem = useMemo<TimelineProps['loadingItem']>(() => ({
        contentPrimaryText: <Skeleton width={'50%'} sx={{minHeight: 30}}/>,
        contentSecondaryText: <Skeleton width={'40%'} sx={{minHeight: 20, marginBlockStart: 0.5,}}/>,
        oppositeContentPrimaryText: <Skeleton width={'70%'} sx={{minHeight: 20, marginInlineStart: 'auto'}}/>,
        oppositeContentSecondaryText: <Skeleton width={'80%'} sx={{minHeight: 20, marginInlineStart: 'auto', marginBlockStart: 0.5,}}/>,
    }), [])

    /**
     * Sets or removes the [replacement] value based on the [key] value.
     */
    const onTimelineItemClicked = useCallback<Required<TimelineProps>['onItemClick']>((key) => {
        setReplacement((prevState) => {
            if (prevState?.key === key) {
                return undefined;
            }
            return data.find(item => item.key === key);
        })
    }, [data])

    /**
     * Creates the styles for the container boxes.
     * @template Theme                           The theme Template
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback<ThemeMixin<Theme>>((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
        gap: 2,
        display: 'flex',
        flexDirection: {xs: 'column', md: 'row'},
        alignItems: 'center',
        justifyContent: "space-between",
    }), []);

    return (
        <>
            <Dialog
                {...dialogProps}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600}>
                    History of Field
                </DialogTitle>
                <DialogContent>
                    <DialogContentText variant={'body1'}>
                        The following is the history of the field. You may select one of the records to replace the current value of the
                        field.
                    </DialogContentText>
                    <Box sx={boxMixin}>
                        <Typography variant={'body1'}>
                            {state?.data?.fieldLabel ?? "Field"}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "end", gap: 1,}}>
                            <Chip
                                size={'medium'}
                                variant={!!replacement ? 'outlined' : 'filled'}
                                color={'primary'}
                                label={state?.data?.currentValue?.title ?? "--"}
                            />
                            {
                                !!replacement &&
                                <ArrowForward sx={{padding: 0.25}}/>
                            }
                            {
                                !!replacement &&
                                <Chip
                                    size={'medium'}
                                    variant={'filled'}
                                    color={'primary'}
                                    label={replacement?.title ?? "--"}
                                />
                            }
                        </Box>
                    </Box>
                    <TextField
                        fullWidth
                        variant={'outlined'}
                        label={'Keyword Search'}
                        value={keywords}
                        onChange={e => setKeywords(e.target.value)}
                        sx={{marginBlockEnd: 3}}
                        InputProps={{
                            endAdornment: <SearchRounded color={'primary'}/>
                        }}
                    />
                    {/*@ts-ignore*/}
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        hasMore={hasMoreToLoad}
                        useWindow={false}
                        threshold={50}
                    >
                        {
                            (searchData.loading || !!timelineItems.length) &&
                            <Timeline
                                title={'History Timeline'}
                                position={'right'}
                                color={'secondary'}
                                items={timelineItems}
                                selectedItemKeys={selectedTimelineItemsKeys}
                                onItemClick={onTimelineItemClicked}
                                loading={searchData.loading}
                                loadingItemsCount={searchData.paginationInfo?.pageSize ?? 3}
                                loadingItem={loadingTimelineItem}
                            />
                        }
                        {
                            !(searchData.loading || !!timelineItems.length) &&
                            <Typography variant={'body2'} width={'100%'} textAlign={'center'} marginBlock={10}>
                                This field does not have any history of changes.
                            </Typography>
                        }
                    </InfiniteScroll>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant={'text'}
                        type={'button'}
                        color={'primary'}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                    >
                        Close
                    </Button>
                    {
                        !!replacement &&
                        <Button
                            variant={'text'}
                            color={'primary'}
                            onClick={() => closeDialog(true, replacement)}
                        >
                            Confirm Replacement
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </>
    )
}

export default FieldLogsHistoryDialog;
